import type { Row } from '@tanstack/react-table';
import { DefaultDate } from 'utils';
import type { TableRowBackgroundContext, TableVariant } from '../types';

function getNestedPrimitive(
  value: unknown,
  path: string[]
): string | number | boolean | null | undefined {
  let cur: unknown = value;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = Reflect.get(cur, key);
  }
  if (cur === null) return null;
  if (typeof cur === 'string' || typeof cur === 'number' || typeof cur === 'boolean') return cur;
  return undefined;
}

/** `original.invalid` when present (no type assertions; unknown row data). */
export function readInvalidFlagFromOriginal(original: unknown): boolean | undefined {
  if (original === null || typeof original !== 'object') return undefined;
  if (!('invalid' in original)) return undefined;
  return Boolean(Reflect.get(original, 'invalid'));
}

/** Matches task-board “unread” row styling used by `Row`. */
export function isTaskRowUnreadForRow(
  row: Row<unknown>,
  loggedInUserId?: string | number
): boolean {
  const original = row.original ?? {};
  const viewedOn = Reflect.get(original, 'viewedOn');
  const assignedToPersonId = getNestedPrimitive(original, ['assignedToPerson', 'id']);
  return (
    (viewedOn === null || String(viewedOn) === DefaultDate) && assignedToPersonId === loggedInUserId
  );
}

export function buildRowBackgroundContext<T>(row: Row<T>): TableRowBackgroundContext<T> {
  const cellValues: Record<string, unknown> = {};
  for (const c of row.getVisibleCells()) {
    const id = c.column.id;
    if (id) cellValues[id] = c.getValue();
  }
  return {
    row,
    original: row.original,
    getCellValue: (columnId: string) => row.getValue(columnId),
    cellValues,
  };
}

type ResolveTableRowCellBackgroundParams<T> = {
  row: Row<T>;
  isSelected: boolean;
  isTaskUnread: boolean;
  isInvalidRow: boolean;
  variant: TableVariant;
  themeBgPrimary: string;
  getRowBackground?: (ctx: TableRowBackgroundContext<T>) => string | undefined;
  expandedRowBackgroundColor?: string;
};

/**
 * Returns a CSS color for table body cells, or undefined to use default theme / `.selected` / etc.
 * Selection uses stylesheet rules — when `isSelected`, this returns undefined so selected styling wins.
 */
export function resolveTableRowCellBackgroundColor<T>(
  p: ResolveTableRowCellBackgroundParams<T>
): string | undefined {
  if (p.isSelected) return undefined;

  if (p.getRowBackground) {
    const ctx = buildRowBackgroundContext(p.row);
    const custom = p.getRowBackground(ctx);
    if (custom !== undefined) return custom;
  }

  if (p.row.getIsExpanded?.() && p.expandedRowBackgroundColor) {
    return p.expandedRowBackgroundColor;
  }

  if (p.isTaskUnread) return '#F2F4FF';
  if (p.isInvalidRow) return p.variant === 'transparent' ? 'transparent' : p.themeBgPrimary;
  return undefined;
}
